// ========================================
// HOOK useAdminAuth
// Gestion de l'authentification admin
// ========================================

import { useState, useEffect } from 'react';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  signOut,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../services/firebase/config';

const useAdminAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Écouter les changements d'état d'authentification
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    // Cleanup
    return () => unsubscribe();
  }, []);

  // Connexion
  const login = async (email, password) => {
    setError(null);
    setLoading(true);
    
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      setUser(userCredential.user);
      return { success: true, user: userCredential.user };
    } catch (err) {
      let errorMessage = 'Erreur de connexion';
      
      switch (err.code) {
        case 'auth/user-not-found':
          errorMessage = 'Aucun compte trouvé avec cet email';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Mot de passe incorrect';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Format d\'email invalide';
          break;
        case 'auth/user-disabled':
          errorMessage = 'Ce compte a été désactivé';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Trop de tentatives. Veuillez réessayer plus tard';
          break;
        case 'auth/invalid-credential':
          errorMessage = 'Email ou mot de passe incorrect';
          break;
        default:
          errorMessage = 'Erreur de connexion. Vérifiez vos identifiants';
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Déconnexion
  const logout = async () => {
    setError(null);
    
    try {
      await signOut(auth);
      setUser(null);
      return { success: true };
    } catch (err) {
      const errorMessage = 'Erreur lors de la déconnexion';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Réinitialisation du mot de passe
  const resetPassword = async (email) => {
    setError(null);
    
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true, message: 'Email de réinitialisation envoyé' };
    } catch (err) {
      let errorMessage = 'Erreur lors de l\'envoi de l\'email';
      
      switch (err.code) {
        case 'auth/user-not-found':
          errorMessage = 'Aucun compte trouvé avec cet email';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Format d\'email invalide';
          break;
        default:
          errorMessage = 'Erreur lors de l\'envoi de l\'email de réinitialisation';
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Vérifier si l'utilisateur est admin (emails autorisés)
  const isAdmin = () => {
    if (!user) return false;
    
    const allowedEmails = [
      'contactgkonate@gmail.com',
      'portfolio.gkonate@gmail.com'
    ];
    
    return allowedEmails.includes(user.email);
  };

  return {
    user,
    loading,
    error,
    login,
    logout,
    resetPassword,
    isAdmin,
    isAuthenticated: !!user
  };
};

export default useAdminAuth;